import { Play, Search } from 'lucide-react';
import { Button, Field, SegmentedControl, Select } from '@/components/ui';
import {
  MAX_COMBINATIONS,
  MAX_LISTED_RESULTS,
  MAX_REJECTION_LEVEL,
  describeTauTest,
  isValidSignificanceLevel,
  planCombinatorialSearch,
} from '@/logic/adjustments';
import { formatConfidence, formatFixed } from '@/logic/statistics';
import type { PointPair, TransformationSolution } from '@/types';
import {
  ALPHA_OPTIONS,
  DEFAULT_ALPHA,
  describeLevel,
  formatCount,
  pluralize,
} from './adjustmentDisplay';

const LEVELS = Array.from({ length: MAX_REJECTION_LEVEL }, (_, i) => i + 1);

export interface DetectionSettingsProps {
  pointPairs: PointPair[];
  /** Current accepted solution, for the τ critical-value preview. */
  solution: TransformationSolution | null;
  rejectionLevel: number;
  runAllMaxLevel: number;
  viewAllSolutions: boolean;
  selectedAlpha: number;
  busy: boolean;
  onRejectionLevelChange: (level: number) => void;
  onRunAllMaxLevelChange: (level: number) => void;
  onViewAllChange: (viewAll: boolean) => void;
  onSelectedAlphaChange: (alpha: number) => void;
  onRunBlunder: () => void;
  onRunAllBlunder: () => void;
  onRunDataSnooping: () => void;
}

/** Run each detection method individually with tuned parameters. */
export const DetectionSettings = ({
  pointPairs,
  solution,
  rejectionLevel,
  runAllMaxLevel,
  viewAllSolutions,
  selectedAlpha,
  busy,
  onRejectionLevelChange,
  onRunAllMaxLevelChange,
  onViewAllChange,
  onSelectedAlphaChange,
  onRunBlunder,
  onRunAllBlunder,
  onRunDataSnooping,
}: DetectionSettingsProps) => {
  const singlePlans = LEVELS.map((level) =>
    planCombinatorialSearch(pointPairs, level, false)
  );
  const allPlans = LEVELS.map((level) =>
    planCombinatorialSearch(pointPairs, level, true)
  );
  const singlePlan = planCombinatorialSearch(pointPairs, rejectionLevel, false);
  const allPlan = planCombinatorialSearch(pointPairs, runAllMaxLevel, true);
  const candidateCount = singlePlan.candidateCount;
  const fit = solution && !solution.error ? solution : null;
  const tau =
    fit && isValidSignificanceLevel(selectedAlpha)
      ? describeTauTest(selectedAlpha, fit.redundancy)
      : null;
  const listed = Math.min(singlePlan.combinations, MAX_LISTED_RESULTS);

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section aria-labelledby="combinatorial-settings" className="space-y-3">
        <div>
          <h3
            id="combinatorial-settings"
            className="text-sm font-semibold text-fg"
          >
            Combinatorial search
          </h3>
          <p className="mt-0.5 text-xs text-fg-muted">
            Tests every rejection set of the chosen size among{' '}
            {pluralize(candidateCount, 'candidate')} (Unity and Fixed pairs in
            the fit). Each run is limited to {formatCount(MAX_COMBINATIONS)}{' '}
            trial fits.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Rejection level" className="min-w-0 flex-1">
            <Select
              aria-label="Single rejection level"
              value={rejectionLevel}
              onChange={(event) =>
                onRejectionLevelChange(Number(event.target.value))
              }
            >
              {singlePlans.map((plan) => (
                <option
                  key={plan.level}
                  value={plan.level}
                  disabled={plan.error !== null}
                >
                  {describeLevel(plan)}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            leadingIcon={<Play />}
            disabled={busy || singlePlan.error !== null}
            disabledReason={
              singlePlan.error ??
              (busy ? 'A calculation is running' : undefined)
            }
            onClick={onRunBlunder}
          >
            Run level {rejectionLevel}
          </Button>
        </div>
        <div className="space-y-1.5">
          <SegmentedControl
            aria-label="Results to list"
            size="sm"
            value={viewAllSolutions ? 'all' : 'best'}
            onValueChange={(value) => onViewAllChange(value === 'all')}
            options={[
              { value: 'best', label: 'Best 10' },
              { value: 'all', label: 'All combinations' },
            ]}
          />
          <p className="text-xs text-fg-muted">
            {viewAllSolutions
              ? singlePlan.combinations > MAX_LISTED_RESULTS
                ? `Lists the best ${formatCount(MAX_LISTED_RESULTS)} of ${formatCount(singlePlan.combinations)} by RMS.`
                : `Lists all ${formatCount(listed)} by RMS.`
              : 'Lists the best 10 by maximum residual.'}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
          <Field label="Levels 1 through" className="min-w-0 flex-1">
            <Select
              aria-label="Maximum rejection level"
              value={runAllMaxLevel}
              onChange={(event) =>
                onRunAllMaxLevelChange(Number(event.target.value))
              }
            >
              {allPlans.map((plan) => (
                <option
                  key={plan.level}
                  value={plan.level}
                  disabled={plan.error !== null}
                >
                  {describeLevel(plan)}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            leadingIcon={<Play />}
            disabled={busy || allPlan.error !== null}
            disabledReason={
              allPlan.error ?? (busy ? 'A calculation is running' : undefined)
            }
            onClick={onRunAllBlunder}
          >
            Run levels 1–{runAllMaxLevel}
          </Button>
        </div>
      </section>

      <section aria-labelledby="snooping-settings" className="space-y-3">
        <div>
          <h3 id="snooping-settings" className="text-sm font-semibold text-fg">
            Data snooping (τ test)
          </h3>
          <p className="mt-0.5 text-xs text-fg-muted">
            Tests each residual component with Pope&apos;s τ and rejects the
            worst pair while any exceeds the critical value.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Confidence" className="min-w-0 flex-1">
            <Select
              aria-label="Data snooping confidence"
              value={selectedAlpha}
              onChange={(event) =>
                onSelectedAlphaChange(Number(event.target.value))
              }
            >
              {ALPHA_OPTIONS.map((alpha) => (
                <option key={alpha} value={alpha}>
                  {formatConfidence(alpha)} · α {alpha}
                  {alpha === DEFAULT_ALPHA ? ' (default)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            leadingIcon={<Search />}
            disabled={busy}
            disabledReason={busy ? 'A calculation is running' : undefined}
            onClick={onRunDataSnooping}
          >
            Run data snooping
          </Button>
        </div>
        {tau && (
          <p className="text-xs text-fg-muted">
            {tau.testable ? (
              <>
                Current fit: τ critical {formatFixed(tau.criticalValue, 2)} at r
                = {tau.redundancy}.{' '}
                {tau.lowPower ? (
                  <span className="text-warning-fg">
                    Low power at this redundancy; a lower confidence detects
                    smaller blunders.
                  </span>
                ) : (
                  'Adequate power at this redundancy.'
                )}
              </>
            ) : (
              <span className="text-warning-fg">
                The current fit has r = {tau.redundancy}; the τ test needs r ≥
                2.
              </span>
            )}
          </p>
        )}
      </section>
    </div>
  );
};
